import React, { FC, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import toast from "react-hot-toast";
import { DATE_TIME_COPY, tripDateRange } from "@1-now/booking-core";
import { useData } from "data/data-provider";
import { changeDate } from "utils/changeDate";
import { useDateAvailability } from "../(car-search-form)/hooks/useDateAvailability";
import { DayWindow, freeHoursLabel, partialKey } from "./availability";
import { CalendarNote, calendarHeader, dayContents } from "./calendarParts";
import PickerLayer from "./PickerLayer";
import TripTile from "./TripTile";

import { useIsSheet } from "./useIsSheet";

export interface DatesPickerBProps {
  /** The car whose availability the calendar shows. */
  selectedCar?: any;
  /** `tiles`: a pick-up and a drop-off tile. `chip`: the compact trigger of the scrolled header bar. */
  variant?: "tiles" | "chip";
}

const shortDay = (d: Date | null) =>
  d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : null;

/** The dates field of the Design B car page: pick-up and drop-off tiles, and this car's availability calendar. */
const DatesPickerB: FC<DatesPickerBProps> = ({
  selectedCar,
  variant = "tiles",
}) => {
  const {
    startDate,
    endDate,
    s_Time,
    e_Time,
    startTime,
    endTime,
    ourCompany,
    pickupLocation,
    setStartDate,
    setEndDate,
    setPartiallyAvailableStartDate,
    setPartiallyAvailableEndDate,
    setPartiallyUnavailableTimes,
    setLongTermRentalNature,
  } = useData();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const sheet = useIsSheet();
  const monthsToShow = sheet ? 1 : 2;
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fullyUnavailableDates, setFullyUnavailableDates] = useState<Date[]>([]);
  const [partialUnavailability, setPartialUnavailability] = useState<Record<string, DayWindow[]>>({});
  const [calendarViewDate, setCalendarViewDate] = useState<Date | null>(null);
  const [tapped, setTapped] = useState<Date | null>(null);
  const { isDateSelectable, isRangeAvailable, isPartialOverlapValid } = useDateAvailability(
    fullyUnavailableDates,
    partialUnavailability,
  );

  // The availability read is loaded when it is first needed, not with the page: it brings the API client
  // with it, which a page in Design A (or a test of one) never uses.
  const fetchUnavailableDates = async (from: Date, to: Date) => {
    const { loadUnavailability } = await import("../(car-search-form)/utils/unavailability");
    return loadUnavailability(
      from,
      to,
      { fleetId: selectedCar?.id, locationId: pickupLocation?.id },
      {
        setLoading: setIsLoading,
        setFullyUnavailable: setFullyUnavailableDates,
        setPartialUnavailability,
        setPartiallyUnavailableTimes,
      },
    );
  };

  // This car's availability, read again when the car or the branch changes.
  // The tiles own the context's blocked hours (the times picker greys its list from them): they drop the
  // previous car's before asking for this one's. The header bar's chip reads the same car and leaves them be.
  // 1NOW-11197: always the two months the wide calendar shows, never the width's own count. Crossing 768px
  // (closing DevTools on the Pay step) used to read it again; by then the renter's own booking could be in it,
  // the times field moved their pick-up off it, and the steps went back to Review for "new" choices.
  useEffect(() => {
    if (variant === "tiles") setPartiallyUnavailableTimes?.({});
    const today = new Date();
    const until = new Date();
    until.setMonth(today.getMonth() + 2);
    fetchUnavailableDates(today, until);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCar?.id, pickupLocation]);

  const handleMonthChange = (date: Date) => {
    setCalendarViewDate(date);
    fetchUnavailableDates(
      new Date(date.getFullYear(), date.getMonth(), 1),
      new Date(date.getFullYear(), date.getMonth() + monthsToShow, 0),
    );
  };

  // The same range rules the calendar in the other designs applies.
  const onChangeDate = (dates: [Date | null, Date | null]) => {
    const startMoment = moment(dates[0]);
    const endMoment = moment(dates[1]);
    const validStartTime = startTime && startTime.trim() !== "" ? startTime : s_Time;
    const validEndTime = endTime && endTime.trim() !== "" ? endTime : e_Time;

    if (!isRangeAvailable(startMoment, endMoment)) {
      changeDate([null, null], setStartDate, setEndDate, validStartTime, validEndTime);
      toast.error("Selected range is fully unavailable.");
      return;
    }
    if (!isPartialOverlapValid(startMoment, endMoment)) {
      changeDate([null, null], setStartDate, setEndDate, validStartTime, validEndTime);
      toast.error("Selected range contains a partially unavailable date in the middle.");
      return;
    }
    changeDate(dates, setStartDate, setEndDate, validStartTime, validEndTime);
    setLongTermRentalNature?.("daily");

    const today = new Date();
    const visibleEnd = new Date(today);
    visibleEnd.setMonth(today.getMonth() + monthsToShow);
    fetchUnavailableDates(today, visibleEnd);
  };

  const clear = () => {
    setStartDate(null);
    setEndDate(null);
    setPartiallyAvailableStartDate?.(null);
    setPartiallyAvailableEndDate?.(null);
  };

  const renderDay = dayContents(fullyUnavailableDates, partialUnavailability, ourCompany, setTapped);

  // The day tapped for its free hours (phones have no hover): "Mon 12 · free until 3:30 PM".
  const tappedHours = tapped ? freeHoursLabel(partialUnavailability[partialKey(tapped)], ourCompany) : null;

  const nights =
    startDate && endDate ? Math.max(1, moment(endDate).startOf("day").diff(moment(startDate).startOf("day"), "days")) : 0;
  const triggerValue = (d: Date | null) => shortDay(d) ?? DATE_TIME_COPY.cellPlaceholder;

  const calendar = (
    <div className="cpb-cal" data-testid="cpb-calendar" aria-busy={isLoading}>
      {startDate && (
        <p className="mb-2 text-xs text-text-color/60">
          {shortDay(startDate)}
          {endDate ? ` → ${shortDay(endDate)}` : ""}
        </p>
      )}
      <div className={isLoading ? "pointer-events-none opacity-60" : ""}>
        <DatePicker
          selected={startDate}
          onChange={onChangeDate}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          monthsShown={monthsToShow}
          showPopperArrow={false}
          inline
          minDate={new Date()}
          openToDate={calendarViewDate || startDate || new Date()}
          filterDate={isDateSelectable}
          renderCustomHeader={calendarHeader(monthsToShow)}
          renderDayContents={renderDay}
          onMonthChange={handleMonthChange}
        />
      </div>

      <CalendarNote tapped={tapped} hours={tappedHours} />
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 text-sm text-text-color">
        {nights > 0 ? (
          <>
            <span className="font-medium">{nights === 1 ? "1 day" : `${nights} days`}</span>
            <span className="block truncate text-xs text-text-color/60">
              {tripDateRange(startDate, endDate)}
            </span>
          </>
        ) : (
          <span className="text-text-color/60">Pick a pick-up and a drop-off day</span>
        )}
        {startDate && (
          <button type="button" onClick={clear} className="mt-0.5 block text-xs font-medium underline">
            Clear dates
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="h-11 shrink-0 rounded-lg bg-primary-brand px-6 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-700"
      >
        Save dates
      </button>
    </div>
  );

  return (
    <div ref={wrapperRef} className="relative" data-field="dates">
      {variant === "chip" ? (
        <TripTile
          layout="chip"
          icon={<CalendarDaysIcon className="h-4 w-4" />}
          label="Dates"
          value={startDate && endDate ? tripDateRange(startDate, endDate) : DATE_TIME_COPY.datePlaceholder}
          empty={!startDate}
          active={open}
          onClick={() => setOpen((was) => !was)}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <TripTile
            layout="field"
            icon={<CalendarDaysIcon className="h-5 w-5" />}
            label={DATE_TIME_COPY.pickUpDate}
            value={triggerValue(startDate)}
            empty={!startDate}
            active={open}
            onClick={() => setOpen((was) => !was)}
          />
          <TripTile
            layout="field"
            icon={<CalendarDaysIcon className="h-5 w-5" />}
            label={DATE_TIME_COPY.dropOffDate}
            value={triggerValue(endDate)}
            empty={!endDate}
            active={open}
            onClick={() => setOpen((was) => !was)}
          />
        </div>
      )}
      <PickerLayer
        open={open}
        onClose={() => {
          setOpen(false);
          setTapped(null);
        }}
        title="Select dates"
        wrapperRef={wrapperRef}
        desktopMode="modal"
        footer={footer}
        name="dates"
      >
        {calendar}
      </PickerLayer>
    </div>
  );
};

export default DatesPickerB;
